/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSyncWorker from "../hooks/useSyncWorker";
import { getEstruturaSincronizacao } from "~services/GlobalServices";
import useUsuario from "~/fetchs/useUsuario";
import { db } from "~/db/db";
import { formatISOWithLocalTime } from "~services/utils/formatDate";

const maxRequests = 4;

const syncRequestSemaphore = {
  active: 0,
  queue: [],
};

const acquireSyncRequestSlot = () => {
  return new Promise((resolve) => {
    const grant = () => {
      syncRequestSemaphore.active += 1;

      let released = false;
      resolve(() => {
        if (released) return;
        released = true;

        syncRequestSemaphore.active = Math.max(0, syncRequestSemaphore.active - 1);
        const next = syncRequestSemaphore.queue.shift();
        if (next) next();
      });
    };

    if (syncRequestSemaphore.active < maxRequests) {
      grant();
      return;
    }

    syncRequestSemaphore.queue.push(grant);
  });
};

const getVendedorLocalStorage = () => {
  const vendedorIdStorage = localStorage.getItem("vendedor-selecionado-id");

  if (!vendedorIdStorage) {
    return null;
  }

  const vendedorIdParsed = Number(vendedorIdStorage);

  return Number.isNaN(vendedorIdParsed) ? null : vendedorIdParsed;
};

const getUltimaSincronizacao = async (table) => {
  const [sincronizacao, quantidade] = await Promise.all([
    db.sincronizacao.where("table").equals(table).first(),
    db.table(table).count(),
  ]);

  if (!sincronizacao?.data || quantidade === 0) return null;

  return formatISOWithLocalTime(new Date(sincronizacao.data));
};

const useDefaultFetch = ({ useGet, enabled, table, processLastFetchTime, setActiveSincronismo, clear = false, remove = false, incremental = false }) => {
  const { usuario } = useUsuario();
  const Acess = Boolean(usuario?.slugAcessos?.includes("sincronismo-por-vendedor"));

  const [vendedorSelecionadoStorage] = useState(getVendedorLocalStorage);

  const vendedorId = useMemo(() => {
    if (!Acess) return null;
    return vendedorSelecionadoStorage;
  }, [Acess, vendedorSelecionadoStorage]);

  const [loading, setLoading] = useState(true);

  const [newEnabled, setNewEnabled] = useState(false);
  const [finished, setFinished] = useState(false);

  const slotReleaseRef = useRef(null);
  const slotRequestedRef = useRef(false);

  const { save, removeIds } = useSyncWorker({ table, setLoading, setFinished, enabled });

  const [dataAtualizacao, setDataAtualizacao] = useState(null);
  const inicioRequestRef = useRef(null);

  const listRequest = useGet(newEnabled, 0, 0, (clear ? null : dataAtualizacao), vendedorId);

  const getDados = (data) => Array.isArray(data) ? data : Object.values(data);

  const releaseSyncSlot = useCallback(() => {
    if (slotReleaseRef.current) {
      slotReleaseRef.current();
      slotReleaseRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      releaseSyncSlot();
      return;
    }

    let cancelled = false;

    const requestSlot = async () => {
      if (slotRequestedRef.current || newEnabled || finished) return;

      slotRequestedRef.current = true;
      const release = await acquireSyncRequestSlot();

      if (cancelled) {
        release();
        return;
      }

      if (incremental && !clear) {
        try {
          setDataAtualizacao(await getUltimaSincronizacao(table));
        } catch (error) {
          console.log(error);
          setDataAtualizacao(null);
        }

        if (cancelled) {
          release();
          return;
        }
      }

      inicioRequestRef.current = new Date();
      slotReleaseRef.current = release;
      setNewEnabled(true);
    };

    requestSlot();

    return () => {
      cancelled = true;
    };
  }, [enabled, newEnabled, finished, releaseSyncSlot]);

  const handleData = useCallback(
    (data) => {
      const options = incremental ? { dataSincronizacao: inicioRequestRef.current, quantidadeTotal: true } : undefined;
      save(data, listRequest.clearCache, true, clear, options);
      processLastFetchTime?.();

    },
    [clear, incremental, save]
  );

  useEffect(() => {
    if (listRequest.data?.data && enabled && !listRequest.isFetching && !listRequest.isRefetching) {
      const data = getDados(listRequest.data.data);
      handleData(data);
      setNewEnabled(false);
    }
  }, [listRequest.data, enabled, listRequest.isFetching, listRequest.isRefetching]);

  useEffect(() => {
    const settled =
      enabled &&
      !listRequest.isFetching &&
      !listRequest.isRefetching &&
      (listRequest.isSuccess || listRequest.isError);

    if (settled) {
      releaseSyncSlot();
    }
  }, [
    enabled,
    listRequest.isFetching,
    listRequest.isRefetching,
    listRequest.isSuccess,
    listRequest.isError,
    releaseSyncSlot,
  ]);

  useEffect(() => {
    if (listRequest.isFetching && !finished) {
      if (typeof setActiveSincronismo === "function") {
        const estrutura = getEstruturaSincronizacao(table);
        if (estrutura) setActiveSincronismo(estrutura.nome)
      }
    }
  }, [listRequest.isFetching]);

  useEffect(() => {
    if (listRequest.data?.existentes?.length >= 0 && enabled && remove) {
      removeIds(listRequest.data.existentes, clear);
    }
  }, [listRequest.data?.existentes]);

  useEffect(() => {
    if (listRequest.isError) {
      setLoading(false);
      setFinished(true);
    }
  }, [listRequest.isError]);

  useEffect(() => {
    return () => {
      releaseSyncSlot();
    };
  }, [releaseSyncSlot]);

  return { loading, finished };
};

export default useDefaultFetch;
